/**
 * Authentication Hook
 *
 * Provides access to authenticated user information and organization context.
 * Use this hook instead of hardcoded user/organization IDs.
 *
 * @example
 * ```tsx
 * const { user, organizationId, isAuthenticated, isLoading } = useAuth();
 *
 * if (isLoading) return <Loading />;
 * if (!isAuthenticated) return <Login />;
 *
 * // Use organizationId in your queries
 * const { data } = useMyDataHook({ organization_id: organizationId });
 * ```
 */

import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectUser, selectIsAuthenticated, selectAuthStatus } from '../store/slices/auth';
import type { RootState } from '../store';
import { useUserProfiles } from '../api/generated_hooks/generated-user_profiles-hooks.js';

export interface UseAuthResult {
  /** Authenticated user object (null if not authenticated) */
  user: {
    id: string;
    email?: string;
    name?: string | null;
  } | null;

  /** User's organization ID (null if not authenticated or no org assigned) */
  organizationId: string | null;

  /** Whether user is authenticated */
  isAuthenticated: boolean;

  /** Whether auth state is still loading */
  isLoading: boolean;

  /** Whether auth has failed */
  isError: boolean;
}

/**
 * Hook to access authenticated user and organization context
 *
 * IMPORTANT: Always check `isAuthenticated` before using `user` or `organizationId`
 */
export function useAuth(): UseAuthResult {
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authStatus = useSelector(selectAuthStatus);

  // Fetch user profile from database to get organization_id
  // NOTE: organization_id is stored in user_profiles table, NOT in JWT metadata
  const { data: userProfile, isLoading: isLoadingProfile } = useUserProfiles(
    user?.id || ''
  );

  // Extract organization_id from user profile
  const organizationId = useMemo(() => {
    if (!userProfile) return null;
    return userProfile.organization_id || null;
  }, [userProfile]);

  return {
    user: user ? {
      id: user.id,
      email: user.email,
      name: (user as any).user_metadata?.name || (user as any).user_metadata?.display_name || null
    } : null,
    organizationId,
    isAuthenticated,
    isLoading: authStatus === 'loading' || isLoadingProfile,
    isError: authStatus === 'failed'
  };
}

/**
 * Hook to get organization ID with loading state
 *
 * Throws an error if user is not authenticated.
 * Use this when organization_id is required for the component to work.
 *
 * @throws Error if user is not authenticated
 *
 * @example
 * ```tsx
 * const organizationId = useRequireOrganizationId();
 * // organizationId is guaranteed to be a string here
 * ```
 */
export function useRequireOrganizationId(): string {
  const { organizationId, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    throw new Error('Auth is still loading');
  }

  if (!isAuthenticated) {
    throw new Error('User must be authenticated');
  }

  if (!organizationId) {
    throw new Error('User does not have an organization assigned');
  }

  return organizationId;
}

/**
 * Hook to get user ID with loading state
 *
 * Throws an error if user is not authenticated.
 * Use this when user_id is required for the component to work.
 *
 * @throws Error if user is not authenticated
 *
 * @example
 * ```tsx
 * const userId = useRequireUserId();
 * // userId is guaranteed to be a string here
 * ```
 */
export function useRequireUserId(): string {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    throw new Error('Auth is still loading');
  }

  if (!isAuthenticated) {
    throw new Error('User must be authenticated');
  }

  if (!user) {
    throw new Error('User object is missing');
  }

  return user.id;
}
